import {
  useActivation,
  useAnnouncements,
  useComposer,
  useMessages,
  useResolvedTheme,
  useShakeHint,
  useUnreadChangelog,
  useUnreadMessages,
  useWherd,
  timeAgo,
  formatMessageTime,
  type MessageThread,
  type WherdView,
} from "@wherdkit/core";
import { useEffect, useRef, useState } from "react";
import {
  FlatList,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useColorScheme,
  useWindowDimensions,
  View,
} from "react-native";
import { FeatureBoard } from "./FeatureBoard";
import { WhatsNew } from "./WhatsNew";

// Optional native bottom-sheet. When the host app has
// @lodev09/react-native-true-sheet installed we present the panel in a real
// native sheet — correct safe-area insets, keyboard handling, and no Android
// clipping. If it isn't installed we fall back to React Native's <Modal>.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let TrueSheet: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  TrueSheet = require("@lodev09/react-native-true-sheet").TrueSheet;
} catch {
  TrueSheet = null;
}

function useToks() {
  const theme = useResolvedTheme(useColorScheme() ?? "light");
  return {
    surface: theme.colors.background,
    text: theme.colors.foreground,
    sub: theme.colors.mutedForeground,
    border: theme.colors.border,
    chip: theme.colors.muted,
    field: theme.colors.background,
    fieldBorder: theme.colors.border,
    accent: theme.colors.primary,
    accentFg: theme.colors.primaryForeground,
    radius: theme.radius,
  };
}

function MsgGlyph({ color, size = 24 }: { color: string; size?: number }) {
  const w = size, h = Math.round(size * 0.84);
  return (
    <View style={{ width: w, height: h + 4, alignItems: "center", justifyContent: "center" }}>
      <View style={{ width: w, height: h, borderRadius: Math.round(size * 0.28), backgroundColor: color }} />
      <View style={{ position: "absolute", bottom: 0, left: Math.round(size * 0.22), width: Math.round(size * 0.34), height: Math.round(size * 0.34), backgroundColor: color, transform: [{ rotate: "45deg" }] }} />
    </View>
  );
}

export interface FabPosition {
  /** Which corner the launcher floats in. Default "bottom-right". */
  placement?: "bottom-right" | "bottom-left";
  /** Distance from the screen edges. Bump `bottom` to clear a tab bar. */
  offset?: { bottom?: number; side?: number };
}

// `force` renders the launcher regardless of the server `activation.fab`
// toggle — for placing it on a specific screen (the global toggle still governs
// the built-in launcher inside <MessagesWidget>).
export function MessagesFAB({
  label,
  placement,
  offset,
  force,
}: { label?: string; force?: boolean } & FabPosition) {
  const { open } = useWherd();
  const t = useToks();
  const unreadMessages = useUnreadMessages();
  const unreadChangelog = useUnreadChangelog();
  const unread = unreadMessages + unreadChangelog;
  const activation = useActivation();
  if (!force && !activation.fab) return null;
  const withLabel = !!label;

  // Open straight to whichever tab the badge is about (messages take priority).
  const target: WherdView =
    unreadMessages > 0 ? "messages" : unreadChangelog > 0 ? "changelog" : "messages";

  const bottom = offset?.bottom ?? 32;
  const side = offset?.side ?? 24;
  const sideStyle =
    (placement ?? "bottom-right") === "bottom-left" ? { left: side } : { right: side };

  return (
    <Pressable
      onPress={() => open(target)}
      style={{ position: "absolute", bottom, ...sideStyle, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: withLabel ? 8 : 0, height: withLabel ? 48 : 56, width: withLabel ? undefined : 56, paddingHorizontal: withLabel ? 20 : 0, borderRadius: withLabel ? t.radius : 999, backgroundColor: t.accent, shadowColor: "#0e1525", shadowOpacity: 0.3, shadowRadius: 15, shadowOffset: { width: 0, height: 8 }, elevation: 8 }}
    >
      <MsgGlyph color={t.accentFg} />
      {withLabel ? <Text style={{ color: t.accentFg, fontWeight: "600", fontSize: 14 }}>{label}</Text> : null}
      {unread > 0 ? (
        <View style={{ position: "absolute", top: -2, right: -2, minWidth: 18, height: 18, paddingHorizontal: 5, borderRadius: 999, backgroundColor: "#ef4444", alignItems: "center", justifyContent: "center", borderWidth: 2, borderColor: t.surface }}>
          <Text style={{ color: "#fff", fontSize: 11, fontWeight: "700" }}>{unread}</Text>
        </View>
      ) : null}
    </Pressable>
  );
}

const MSG_PLACEHOLDER = "What's on your mind?";

// Compose a brand-new thread.
function Composer({ onSent, collectEmail }: { onSent: () => void; collectEmail?: boolean }) {
  const { config, user } = useWherd();
  const t = useToks();
  const form = useComposer();
  const copy = config?.copy ?? {};
  // Hide the email field whenever the host has identified a user at all — we can
  // already route replies via their identity, so we never need to ask. Only
  // anonymous users (no user object) get the field. `collectEmail` overrides.
  const showEmail = collectEmail ?? !user;
  const fieldStyle = { borderRadius: 12, borderWidth: 1.5, borderColor: t.fieldBorder, backgroundColor: t.field, color: t.text } as const;

  if (form.submitted) {
    return (
      <View style={{ alignItems: "center", paddingVertical: 18 }}>
        <View style={{ width: 62, height: 62, borderRadius: 31, backgroundColor: "#dcfce7", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
          <View style={{ width: 14, height: 24, borderColor: "#22c55e", borderRightWidth: 4, borderBottomWidth: 4, transform: [{ rotate: "45deg" }], marginTop: -6 }} />
        </View>
        <Text style={{ fontSize: 19, fontWeight: "800", color: t.text, marginBottom: 6 }}>{copy.thanksMessage ?? "Thanks — we got it!"}</Text>
        <Text style={{ fontSize: 14, color: t.sub, lineHeight: 20, textAlign: "center", marginBottom: 18 }}>
          {form.queued ? "You're offline — we'll send it once you reconnect." : "We'll reply right here — check back anytime."}
        </Text>
        <Pressable onPress={() => { form.reset(); onSent(); }}>
          <Text style={{ fontSize: 14, fontWeight: "700", color: t.accent }}>Done</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View>
      <TextInput
        multiline value={form.body} onChangeText={form.setBody}
        placeholder={copy.messagePlaceholder ?? MSG_PLACEHOLDER} placeholderTextColor={t.sub}
        style={{ ...fieldStyle, minHeight: 104, padding: 13, fontSize: 14.5, textAlignVertical: "top", marginBottom: 10 }}
      />
      {showEmail ? (
        <TextInput
          value={form.email} onChangeText={form.setEmail} placeholder="Email (optional)" placeholderTextColor={t.sub}
          autoCapitalize="none" keyboardType="email-address"
          style={{ ...fieldStyle, paddingHorizontal: 13, paddingVertical: 11, fontSize: 14 }}
        />
      ) : null}
      {form.error ? <Text style={{ marginTop: 8, color: "#ef4444", fontSize: 13 }}>{form.error}</Text> : null}
      <Pressable onPress={() => void form.submit()} disabled={form.submitting} style={{ marginTop: 14, paddingVertical: 12, borderRadius: 11, backgroundColor: t.accent, alignItems: "center", opacity: form.submitting ? 0.7 : 1 }}>
        <Text style={{ color: t.accentFg, fontWeight: "700", fontSize: 14.5 }}>{form.submitting ? "Sending…" : copy.submitLabel ?? "Send"}</Text>
      </Pressable>
    </View>
  );
}

// A single thread: original + replies + a reply box.
function ThreadDetail({ thread, onBack }: { thread: MessageThread; onBack: () => void }) {
  const t = useToks();
  const { reply, markRead } = useMessages();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  // Mark read on open AND whenever a new reply arrives while the thread is open
  // (depend on unread) — otherwise a live message leaves a stale unread dot in
  // the list even though you already saw it.
  useEffect(() => {
    if (thread.unread > 0) void markRead(thread.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id, thread.unread]);

  const turns = [
    {
      author: (thread.origin === "admin" ? "admin" : "user") as "user" | "admin",
      body: thread.body,
      createdAt: thread.createdAt,
    },
    ...thread.replies,
  ];
  // Inverted list → newest first; it stays pinned to the bottom and new
  // messages (own or admin) land at the bottom with no manual scrolling.
  const data = [...turns].reverse();

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    const ok = await reply(thread.id, draft.trim());
    setSending(false);
    if (ok) setDraft("");
  };

  return (
    <View style={{ flex: 1 }}>
      <Pressable onPress={onBack} style={{ marginBottom: 12 }}>
        <Text style={{ fontSize: 13, fontWeight: "700", color: t.accent }}>← All messages</Text>
      </Pressable>
      <FlatList
        data={data}
        inverted
        keyExtractor={(m, i) => `${m.createdAt}-${i}`}
        style={{ flex: 1 }}
        contentContainerStyle={{ gap: 8, paddingVertical: 6 }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        renderItem={({ item: m }) => {
          const mine = m.author === "user";
          return (
            <View style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "85%" }}>
              <View style={{ backgroundColor: mine ? t.accent : t.chip, borderRadius: 14, padding: 11 }}>
                <Text style={{ fontSize: 14, color: mine ? t.accentFg : t.text, lineHeight: 19 }}>{m.body}</Text>
              </View>
              <Text style={{ fontSize: 11, color: t.sub, marginTop: 3, marginHorizontal: 4, alignSelf: mine ? "flex-end" : "flex-start" }}>{formatMessageTime(m.createdAt)}</Text>
            </View>
          );
        }}
      />
      <View style={{ flexDirection: "row", gap: 8, marginTop: 12 }}>
        <TextInput
          value={draft} onChangeText={setDraft} placeholder="Reply…" placeholderTextColor={t.sub}
          style={{ flex: 1, borderRadius: 11, borderWidth: 1.5, borderColor: t.fieldBorder, backgroundColor: t.field, color: t.text, paddingHorizontal: 13, paddingVertical: 10, fontSize: 14 }}
        />
        <Pressable onPress={() => void send()} disabled={sending || !draft.trim()} style={{ paddingHorizontal: 16, justifyContent: "center", borderRadius: 11, backgroundColor: t.accent, opacity: sending || !draft.trim() ? 0.6 : 1 }}>
          <Text style={{ color: t.accentFg, fontWeight: "700", fontSize: 14 }}>Send</Text>
        </Pressable>
      </View>
    </View>
  );
}

// Messages tab: your threads, a composer, and per-thread conversations.
function MessagesView({ collectEmail }: { collectEmail?: boolean }) {
  const t = useToks();
  const { threads, loading, refresh } = useMessages();
  const [mode, setMode] = useState<"list" | "compose" | { id: string }>("list");

  // Start first-timers straight in the composer.
  useEffect(() => {
    if (!loading && threads.length === 0) setMode("compose");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (typeof mode === "object") {
    const thread = threads.find((x) => x.id === mode.id);
    if (thread) return <ThreadDetail thread={thread} onBack={() => setMode("list")} />;
  }
  if (mode === "compose") {
    return (
      <View style={{ flex: 1 }}>
        {threads.length > 0 ? (
          <Pressable onPress={() => setMode("list")} style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 13, fontWeight: "700", color: t.accent }}>← All messages</Text>
          </Pressable>
        ) : null}
        <Composer collectEmail={collectEmail} onSent={() => { void refresh(); setMode("list"); }} />
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <Pressable onPress={() => setMode("compose")} style={{ paddingVertical: 12, borderRadius: 11, backgroundColor: t.accent, alignItems: "center", marginBottom: 14 }}>
        <Text style={{ color: t.accentFg, fontWeight: "700", fontSize: 14.5 }}>Contact us</Text>
      </Pressable>
      {threads.length === 0 ? (
        <Text style={{ color: t.sub, fontSize: 14, textAlign: "center", paddingVertical: 18 }}>No messages yet.</Text>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 8 }}>
          {threads.map((th) => {
            const last = th.replies.length ? th.replies[th.replies.length - 1]!.createdAt : th.createdAt;
            const meta = th.replies.length > 0 ? `${th.replies.length} repl${th.replies.length === 1 ? "y" : "ies"}` : "Sent";
            return (
            <Pressable key={th.id} onPress={() => setMode({ id: th.id })} style={{ backgroundColor: t.chip, borderRadius: 12, padding: 13, flexDirection: "row", alignItems: "center", gap: 10 }}>
              <View style={{ flex: 1 }}>
                <Text numberOfLines={1} style={{ fontSize: 14, fontWeight: "600", color: t.text }}>{th.body}</Text>
                <Text style={{ fontSize: 12, color: t.sub, marginTop: 2 }}>{meta} · {timeAgo(last)}</Text>
              </View>
              {th.unread > 0 ? (
                <View style={{ minWidth: 18, height: 18, paddingHorizontal: 5, borderRadius: 999, backgroundColor: "#ef4444", alignItems: "center", justifyContent: "center" }}>
                  <Text style={{ color: "#fff", fontSize: 11, fontWeight: "700" }}>{th.unread}</Text>
                </View>
              ) : null}
            </Pressable>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

const SEVERITY: Record<string, { bg: string; fg: string }> = {
  info: { bg: "#eef0ff", fg: "#3730a3" },
  warning: { bg: "#fef3c7", fg: "#92400e" },
  critical: { bg: "#fee2e2", fg: "#991b1b" },
};

// Announcement banners (severity-styled, dismissible) — shown above the tabs.
function AnnouncementBanners() {
  const { announcements, dismiss } = useAnnouncements();
  if (!announcements.length) return null;
  return (
    <View style={{ gap: 8, marginBottom: 14 }}>
      {announcements.map((a) => {
        const s = SEVERITY[a.severity] ?? SEVERITY.info!;
        return (
          <View key={a.id} style={{ backgroundColor: s.bg, borderRadius: 12, padding: 12, flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 14, fontWeight: "800", color: s.fg, marginBottom: a.body ? 3 : 0 }}>{a.title}</Text>
              {a.body ? <Text style={{ fontSize: 13, color: s.fg, lineHeight: 18 }}>{a.body}</Text> : null}
            </View>
            <Pressable onPress={() => dismiss(a.id)} hitSlop={8}>
              <Text style={{ color: s.fg, fontSize: 16, fontWeight: "700" }}>×</Text>
            </Pressable>
          </View>
        );
      })}
    </View>
  );
}

// In-panel shake hint (first time, when shake is enabled).
function ShakeBanner() {
  const t = useToks();
  const { visible, dismiss } = useShakeHint();
  if (!visible) return null;
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 10, backgroundColor: t.chip, borderRadius: 12, padding: 12, marginBottom: 14 }}>
      <Text style={{ flex: 1, fontSize: 13, color: t.sub, lineHeight: 18 }}>
        Shake your device anytime to reach us — no button needed.
      </Text>
      <Pressable onPress={dismiss} hitSlop={8}>
        <Text style={{ color: t.sub, fontSize: 16, fontWeight: "700" }}>×</Text>
      </Pressable>
    </View>
  );
}

const TAB_LABEL: Record<WherdView, string> = { messages: "Messages", featureRequests: "Ideas", changelog: "What's New" };

// The panel contents — banners, tab switcher, and the active view. Chrome
// (surface, corner radius, padding, grab handle) is supplied by whichever
// presenter wraps it: the native sheet or the Modal fallback.
function PanelBody({ collectEmail }: { collectEmail?: boolean }) {
  const { view, setView, close, config } = useWherd();
  const t = useToks();
  const modules = config?.enabledModules ?? { messages: true, featureRequests: true, changelog: false };
  const tabs: WherdView[] = [];
  if (modules.messages) tabs.push("messages");
  if (modules.featureRequests) tabs.push("featureRequests");
  if (modules.changelog) tabs.push("changelog");

  // Per-tab unread so it's clear which tab the badge points to.
  const unreadByTab: Record<WherdView, number> = {
    messages: useUnreadMessages(),
    featureRequests: 0,
    changelog: useUnreadChangelog(),
  };

  return (
    <View style={{ flex: 1 }}>
      <View style={{ alignItems: "center", paddingBottom: 12, marginTop: -4 }}>
        <View style={{ width: 38, height: 5, borderRadius: 999, backgroundColor: t.border }} />
      </View>
      <AnnouncementBanners />
      <ShakeBanner />
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <View style={{ flexDirection: "row", gap: 2, backgroundColor: t.chip, borderRadius: 999, padding: 4 }}>
          {tabs.map((tab) => {
            const on = view === tab;
            // Dot only when another tab has unread (Ideas is always 0).
            const dot = !on && unreadByTab[tab] > 0;
            return (
              <Pressable key={tab} onPress={() => setView(tab)} style={{ flexDirection: "row", alignItems: "center", gap: 6, paddingVertical: 7, paddingHorizontal: 13, borderRadius: 999, backgroundColor: on ? t.surface : "transparent" }}>
                <Text style={{ fontSize: 13.5, fontWeight: "700", color: on ? t.text : t.sub }}>{TAB_LABEL[tab]}</Text>
                {dot ? <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: t.accent }} /> : null}
              </Pressable>
            );
          })}
        </View>
        <Pressable onPress={close} hitSlop={12} style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: t.chip, alignItems: "center", justifyContent: "center" }}>
          <Text style={{ color: t.sub, fontSize: 22, lineHeight: 24 }}>×</Text>
        </Pressable>
      </View>

      <View style={{ flex: 1 }}>
        {view === "featureRequests" ? <FeatureBoard /> : view === "changelog" ? <WhatsNew /> : <MessagesView collectEmail={collectEmail} />}
      </View>
    </View>
  );
}

// Native bottom-sheet presenter (when @lodev09/react-native-true-sheet is
// available). Driven imperatively off `isOpen`; a swipe-to-dismiss syncs the
// SDK state back via close().
function SheetPresenter({ isOpen, close, collectEmail }: { isOpen: boolean; close: () => void; collectEmail?: boolean }) {
  const t = useToks();
  const { height } = useWindowDimensions();
  // A definite panel height (not flex on the sheet itself — TrueSheet doesn't
  // propagate a height to flex children) so the conversation can fill + scroll.
  const panelH = Math.round(height * 0.8);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useRef<any>(null);
  // Don't issue a dismiss before the sheet has ever been presented — calling
  // present()/dismiss() out of turn rejects with "no sheet found with tag …".
  const presentedOnce = useRef(false);

  useEffect(() => {
    const sheet = ref.current;
    if (!sheet) return;
    // present()/dismiss() return promises that reject if the sheet isn't in the
    // expected state (already dismissed, mid-animation). Those are benign here,
    // so swallow them rather than let Sentry capture an unhandled rejection.
    let result: { catch?: (cb: () => void) => void } | undefined;
    if (isOpen) {
      presentedOnce.current = true;
      result = sheet.present?.();
    } else if (presentedOnce.current) {
      result = sheet.dismiss?.();
    }
    result?.catch?.(() => {});
  }, [isOpen]);

  return (
    <TrueSheet
      ref={ref}
      detents={["auto"]}
      cornerRadius={24}
      backgroundColor={t.surface}
      grabber={false}
      dimmed
      onDidDismiss={() => {
        if (isOpen) close();
      }}
    >
      <View style={{ height: panelH, padding: 24, paddingBottom: 38 }}>
        <PanelBody collectEmail={collectEmail} />
      </View>
    </TrueSheet>
  );
}

// Modal fallback presenter (no native sheet installed). Anchors the panel to
// the bottom of the screen with a tap-to-dismiss scrim.
function ModalPresenter({ isOpen, close, collectEmail }: { isOpen: boolean; close: () => void; collectEmail?: boolean }) {
  const t = useToks();
  const { height } = useWindowDimensions();
  const panelH = Math.round(height * 0.8);
  return (
    <Modal visible={isOpen} transparent animationType="slide" onRequestClose={close}>
      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : "height"} style={{ flex: 1 }}>
        <Pressable onPress={close} style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.4)", justifyContent: "flex-end" }}>
          <Pressable
            onPress={(e) => e.stopPropagation()}
            // Definite height so the panel's flex children fill + scroll.
            style={{ height: panelH, backgroundColor: t.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: 38, borderTopWidth: 1, borderColor: t.border }}
          >
            <PanelBody collectEmail={collectEmail} />
          </Pressable>
        </Pressable>
      </KeyboardAvoidingView>
    </Modal>
  );
}

export function MessagesWidget({ fab, collectEmail }: { fab?: { label?: string } & FabPosition; collectEmail?: boolean } = {}) {
  const { isOpen, close } = useWherd();
  // `TrueSheet` is resolved once at module load, so the chosen presenter is
  // stable across renders — no conditional-hook hazard.
  const Presenter = TrueSheet ? SheetPresenter : ModalPresenter;
  return (
    <>
      <MessagesFAB {...fab} />
      <Presenter isOpen={isOpen} close={close} collectEmail={collectEmail} />
    </>
  );
}
